import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, X, MessageCircle } from "lucide-react";
import { useSite } from "@/lib/SiteContext";
import { useT } from "@/lib/i18n";
import { buildWhatsAppUrl } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  organization: "",
  interest: "general",
  message: "",
};

export const ContactDialog = () => {
  const { contactOpen, closeContact, contactDefault, locale } = useSite();
  const { t, d } = useT(locale);
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (contactOpen) {
      setForm({ ...initial, interest: contactDefault?.interest || "general" });
      setStatus("idle");
      setError(null);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [contactOpen, contactDefault]);

  if (!contactOpen) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError(null);
    try {
      await axios.post(`${API}/contact`, { ...form, locale });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail || "error");
    }
  };

  const interestOptions = d.forms.interest_options;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center"
      role="dialog"
      aria-modal="true"
      data-testid="contact-dialog"
    >
      <button
        className="absolute inset-0 bg-[var(--ix-charcoal)]/55 backdrop-blur-sm"
        onClick={closeContact}
        aria-label="Close"
        data-testid="contact-dialog-overlay"
      />
      <div
        className="relative bg-[var(--ix-cream)] w-full md:max-w-2xl md:rounded-sm border border-[var(--ix-line-light)] shadow-2xl max-h-[92vh] overflow-y-auto"
        style={{ color: "var(--ix-night)" }}
      >
        <div className="px-6 md:px-10 py-6 flex items-start justify-between border-b border-[var(--ix-line-light)] sticky top-0 bg-[var(--ix-cream)] z-10">
          <div>
            <div className="ix-mono text-[0.66rem] uppercase tracking-[0.22em] ix-grad-text font-semibold">
              {locale === "es" ? "Diseñar experiencia" : "Design an experience"}
            </div>
            <h3 className="ix-display text-[1.7rem] md:text-[2rem] mt-2 text-[var(--ix-night)] leading-tight">
              {t("forms.contact_title")}
            </h3>
            <p className="text-[0.88rem] text-[var(--ix-muted)] mt-2 max-w-md">
              {t("forms.contact_sub")}
            </p>
          </div>
          <button
            onClick={closeContact}
            className="p-2 -mt-1 -mr-2 hover:bg-[var(--ix-sand)] transition-colors"
            aria-label="Close dialog"
            data-testid="contact-dialog-close"
          >
            <X size={20} />
          </button>
        </div>

        {status === "success" ? (
          <div className="px-6 md:px-10 py-12 text-center" data-testid="contact-success">
            <div className="ix-display text-[2rem] ix-grad-text">
              {locale === "es" ? "Gracias." : "Thank you."}
            </div>
            <p className="mt-3 text-[var(--ix-muted)] max-w-md mx-auto">
              {t("forms.success")}
            </p>
            <a
              href={buildWhatsAppUrl(`Hola, soy ${form.name}. Acabo de escribirles desde la web.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="ix-btn ix-btn-grad mt-7"
              data-testid="contact-success-whatsapp"
            >
              <MessageCircle size={15} />
              {t("nav.whatsapp")}
            </a>
            <button
              onClick={closeContact}
              className="block mx-auto mt-5 ix-link text-[0.85rem] text-[var(--ix-muted)]"
              data-testid="contact-success-close"
            >
              {locale === "es" ? "Cerrar" : "Close"}
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="px-6 md:px-10 py-7 space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label={t("forms.name")} required>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="ix-input"
                  data-testid="contact-name"
                />
              </Field>
              <Field label={t("forms.email")} required>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="ix-input"
                  data-testid="contact-email"
                />
              </Field>
              <Field label={t("forms.phone")}>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  className="ix-input"
                  data-testid="contact-phone"
                />
              </Field>
              <Field label={t("forms.organization")}>
                <input
                  value={form.organization}
                  onChange={update("organization")}
                  className="ix-input"
                  data-testid="contact-org"
                />
              </Field>
            </div>

            <Field label={t("forms.interest")} required>
              <div className="flex flex-wrap gap-2">
                {Object.entries(interestOptions).map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, interest: k }))}
                    className={`px-3 py-1.5 text-[0.82rem] rounded-full border transition-all ${
                      form.interest === k
                        ? "bg-[var(--ix-night)] text-[var(--ix-cream)] border-[var(--ix-night)]"
                        : "bg-transparent text-[var(--ix-night)]/75 border-[var(--ix-line-light)] hover:border-[var(--ix-night)]"
                    }`}
                    data-testid={`contact-interest-${k}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </Field>

            <Field label={t("forms.message")} required>
              <textarea
                required
                rows={4}
                value={form.message}
                onChange={update("message")}
                className="ix-input resize-none"
                data-testid="contact-message"
              />
            </Field>

            {status === "error" && (
              <div className="text-[0.88rem] text-[#C45D3A]" data-testid="contact-error">
                {t("forms.error")}
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 pt-3">
              <a
                href={buildWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[0.85rem] text-[var(--ix-muted)] ix-link inline-flex items-center gap-2"
              >
                <MessageCircle size={14} />
                {locale === "es" ? "O contáctanos directamente" : "Or contact us directly"}
              </a>
              <button
                type="submit"
                disabled={status === "sending"}
                className="ix-btn ix-btn-grad"
                data-testid="contact-submit"
              >
                {status === "sending" ? (
                  <>
                    <Loader2 className="animate-spin" size={15} />
                    {t("forms.sending")}
                  </>
                ) : (
                  t("forms.submit")
                )}
              </button>
            </div>
          </form>
        )}
      </div>
      <style>{`
        .ix-input {
          width: 100%;
          padding: 0.7rem 0.9rem;
          background: var(--ix-paper);
          border: 1px solid var(--ix-line-light);
          border-radius: 2px;
          font-family: 'Manrope', sans-serif;
          font-size: 0.92rem;
          color: var(--ix-night);
          transition: border-color 0.3s, box-shadow 0.3s;
        }
        .ix-input:focus {
          outline: none;
          border-color: var(--ix-night);
          box-shadow: 0 0 0 3px rgba(255,31,143,0.1);
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="ix-mono text-[0.65rem] uppercase tracking-[0.18em] text-[var(--ix-night)]/65 block mb-2">
      {label} {required ? <span className="ix-grad-text font-bold">*</span> : null}
    </span>
    {children}
  </label>
);

export default ContactDialog;
